import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import type {
	IConcreteExternalComponentLoader,
	ExternalComponentSiteConfig,
	IExternalComponentLoader,
	IPartytownLoader,
	IPreventRendererNotSupported,
} from '../types'
import {
	name,
	PartytownLoaderSymbol,
	TrustedExternalComponentLoaderSymbol,
	UntrustedExternalComponentLoaderSymbol,
	PreventRendererNotSupportedSymbol,
} from '../symbols'
import { SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import generateWixCodeUrl from '../utils/generateWixCodeUrl'
import { isPartytownNotSupported } from '../utils/partytownTester'

const fixVeloFileUrl = (url: string) => {
	const matchResult = url.match(/^(\/)?components/)
	return matchResult ? `components/${encodeURIComponent(url.replace(`${matchResult[0]}/`, ''))}` : url
}

export const ExternalComponentLoader = withDependencies(
	[
		named(SiteFeatureConfigSymbol, name),
		PartytownLoaderSymbol,
		UntrustedExternalComponentLoaderSymbol,
		optional(TrustedExternalComponentLoaderSymbol),
		optional(PreventRendererNotSupportedSymbol),
	],
	(
		externalComponentSiteConfig: ExternalComponentSiteConfig,
		partytownLoader: IPartytownLoader,
		untrustedLoader: IConcreteExternalComponentLoader,
		trustedLoader: IConcreteExternalComponentLoader = untrustedLoader,
		preventRendererNotSupported?: IPreventRendererNotSupported
	): IExternalComponentLoader => {
		return {
			async loadComponent({
				compProps,
				url,
				appDefId,
				importKey = 'default',
				id,
				isTrusted,
				onError,
				cacheKillerCounter,
			}) {
				const { wixCodeBundlersUrlData, forceTrustedMode, forceBundlerDevMode } = externalComponentSiteConfig

				const fullUrl = generateWixCodeUrl({
					wixCodeBundlersUrlData,
					url: fixVeloFileUrl(url),
					forceBundlerDevMode,
					cacheKillerCounter,
					appDefId,
				})

				if (forceTrustedMode) {
					return trustedLoader.loadComponent({ compProps, url, importKey, id, fullUrl, onError })
				}

				await partytownLoader.loadPartytown()

				await preventRendererNotSupported?.preventRendererNotSupported?.()

				const loader = isTrusted || (await isPartytownNotSupported()) ? trustedLoader : untrustedLoader

				return loader.loadComponent({ compProps, url, importKey, id, fullUrl, onError })
			},
		}
	}
)
